import React from 'react';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { ActionButton } from 'components/ui/Button';
import Select from 'components/ui/Select';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { MandatoryFieldLabel, ErrorField, Field, StyledSubmitField, StyledForm } from './SearchBooking.styles';
import { useQuery } from '@apollo/client';
import { GET_STATIONS } from 'store/queries';
import { Column, Row } from 'components/shared/Layout';
import { Spinner } from 'admin-components/components';

const SearchByMinors = ({ onSubmitSearch, defaultPeriod, defaultStationUic, defaultUserStation = '' }) => {
  const { data: { stations } = { stations: [] }, loading } = useQuery(GET_STATIONS);

  const filteredStations = stations.filter(({ isEurostarDirect }) => isEurostarDirect);

  const { register, handleSubmit, formState } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { errors } = formState;

  const initialDate = moment().startOf('month');
  const months = new Array(12).fill(initialDate).map((date, index) => {
    const month = moment(date).add(index, 'months');
    const label = month.format('MMMM YYYY');
    const value = month.format('YYYY-MM');

    return { label, value };
  });

  const [{ value: fallbackValue }] = months;
  const { value: defaultValue } = months.find(({ value }) => (defaultPeriod ? value === defaultPeriod : value === fallbackValue));

  if (loading) {
    return (
      <Row height="100vh">
        <Column alignItems="center" justifyContent="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  return (
    <StyledForm data-testid="search-minors-form" onSubmit={handleSubmit(onSubmitSearch)}>
      <Field>
        <MandatoryFieldLabel htmlFor="searchMinorsStation">
          <FormattedMessage id="searchBooking.station" />
        </MandatoryFieldLabel>
        <Select
          {...register('searchMinorsStation', { required: true })}
          id="searchMinorsStation"
          data-cy="search-minors-station"
          data-testid="search-minors-station"
          defaultValue={defaultStationUic || stations.find(({ shortCode }) => shortCode === defaultUserStation.split('_')?.[1])?.uic}
        >
          {filteredStations.map(({ name, uic }) => (
            <option key={name} value={uic} data-testid={`search-minors-station-${uic}`}>
              {name}
            </option>
          ))}
        </Select>
        {errors.searchMinorsStation && (
          <ErrorField id="error-minors-station" data-testid="error-minors-station">
            <FormattedMessage id="searchBooking.minorsStationValidationMessage" />
          </ErrorField>
        )}
      </Field>
      <Field>
        <MandatoryFieldLabel htmlFor="searchMinorsPeriod">
          <FormattedMessage id="searchBooking.departureDate" />
        </MandatoryFieldLabel>
        <Select
          {...register('searchMinorsPeriod', { required: true })}
          id="searchMinorsPeriod"
          data-cy="search-minors-period"
          data-testid="search-minors-period"
          defaultValue={defaultValue}
        >
          {months.map(({ label, value }) => (
            <option key={label} value={value} data-testid={`search-minors-period-${value}`}>
              {label}
            </option>
          ))}
        </Select>
        {errors.searchMinorsPeriod && (
          <ErrorField id="error-minors-period" data-testid="error-minors-period">
            <FormattedMessage id="searchBooking.minorsPeriodValidationMessage" />
          </ErrorField>
        )}
      </Field>
      <StyledSubmitField>
        <ActionButton data-cy="minors-search" data-testid="minors-search" type="submit" disabled={!formState.isValid}>
          <FormattedMessage id="searchBooking.search" />
        </ActionButton>
      </StyledSubmitField>
    </StyledForm>
  );
};

SearchByMinors.propTypes = {
  defaultPeriod: PropTypes.string,
  defaultStationUic: PropTypes.string,
  defaultUserStation: PropTypes.string,
  onSubmitSearch: PropTypes.func,
  defaultJourneyOrigin: PropTypes.string
};

export default SearchByMinors;
